import TileLayer from 'ol/layer/Tile'
import VectorTileLayer from 'ol/layer/VectorTile'
import XYZ from 'ol/source/XYZ'
import { applyStyle } from 'ol-mapbox-style'
import {
  labelsStyleUrl,
  withArcGISToken,
  type LabelsStyleOptions,
} from './arcgis-basemap-style'

/*
 * Esri's raster basemaps, served through ArcGIS Location Platform. The tile path
 * is {z}/{y}/{x} — row before column — which is ArcGIS REST's order, not the
 * {z}/{x}/{y} of an XYZ/OSM endpoint.
 */
const TILE_HOST = 'https://ibasemaps-api.arcgis.com/arcgis/rest/services'

const IMAGERY_SERVICE = 'World_Imagery'

/*
 * The other service ibasemaps-api publishes. It is a ready-made global shaded
 * relief raster, which is what the 3D map's Elevation surface shows in place of
 * the satellite imagery: real hillshading everywhere, rather than a flat fill
 * outside the harvest's own DEM.
 */
const HILLSHADE_SERVICE = 'Elevation/World_Hillshade'

/*
 * Esri publishes World Imagery at z0–19 worldwide and z20–23 only where it holds
 * high-resolution coverage — common over North American farmland, never
 * guaranteed. The view allows 21, so those levels are requested for real rather
 * than faked by upscaling z19: sharp imagery where it exists, empty tiles where
 * it does not.
 */
const MAX_ZOOM = 21

/*
 * Credit required by the ArcGIS terms of use, copied from each service's own
 * `copyrightText` (MapServer?f=json) rather than written by hand. Deliberately not
 * run through i18n — it is a legal attribution naming specific organisations, and
 * translating it would misstate the credit. OL renders it in the Attribution
 * control that map-provider and split-map-provider install.
 */
const IMAGERY_ATTRIBUTION =
  'Powered by <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a> — Source: Esri, Vantor, Earthstar Geographics, and the GIS User Community'
const HILLSHADE_ATTRIBUTION =
  'Powered by <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a> — Sources: Esri, Vantor, Airbus DS, USGS, NGA, NASA, CGIAR, N Robinson, NCEAS, NLS, OS, NMA, Geodatastyrelsen, Rijkswaterstaat, GSA, Geoland, FEMA, Intermap, and the GIS user community'
const LABELS_ATTRIBUTION =
  'Sources: Esri, TomTom, Garmin, FAO, NOAA, USGS, © OpenStreetMap contributors, and the GIS User Community'

/*
 * Base imagery sits below every data layer. Those all use a zIndex of 1 or more
 * (see wms-layer, boundary-layer, fields-overview and friends), so negative
 * values keep the base map underneath no matter what order components mount in.
 */
const IMAGERY_Z_INDEX = -2
const LABELS_Z_INDEX = -1

/*
 * Marks a layer as part of the base map rather than data. The 3D map's surface
 * selector hides these to show elevation colouring on its own, and tagging them
 * explicitly beats inferring it from the negative zIndex above: the zIndex is a
 * stacking decision that could reasonably change, whereas this says what the
 * layer *is*. Bing sets the same property (see components/map/bing-maps).
 */
export const BASE_LAYER_PROPERTY = 'farmtrxBaseLayer'

/**
 * Marks the shaded-relief half of the base map, so the 3D surface selector can
 * swap it in for the imagery rather than merely hiding things.
 */
export const RELIEF_LAYER_PROPERTY = 'farmtrxReliefLayer'

const tileUrl = (service: string, token: string) =>
  `${TILE_HOST}/${service}/MapServer/tile/{z}/{y}/{x}?token=${token}`

export type BaseMapLayer = TileLayer<XYZ> | VectorTileLayer

type TileLayerSpec = {
  className: string
  title: string
  attributions: string
  zIndex: number
}

const createTileLayer = (
  service: string,
  { className, title, attributions, zIndex }: TileLayerSpec,
) => {
  const layer = new TileLayer({
    className,
    zIndex,
    source: new XYZ({
      // The url arrives with the session token; see applyArcGISImagerySource.
      maxZoom: MAX_ZOOM,
      attributions,
      // No preload: a session covers unlimited tiles, but each one is still a
      // request over the wire, so only what the viewport needs is fetched. No
      // crossOrigin either — nothing in the app reads the map canvas back, and
      // requiring CORS would turn a missing header into a blank base map.
    }),
  })
  layer.set('title', title)
  return layer
}

/**
 * The imagery half of the base map, without a source url. Depends on nothing
 * that varies at runtime — no locale, no tenant — so a caller can build it once
 * and keep it, calling `applyArcGISImagerySource` to point it at a session.
 */
export const createArcGISImageryLayer = (): TileLayer<XYZ> => {
  const layer = createTileLayer(IMAGERY_SERVICE, {
    className: 'ol-arcgis-imagery-layer',
    title: 'ArcGIS World Imagery',
    attributions: IMAGERY_ATTRIBUTION,
    zIndex: IMAGERY_Z_INDEX,
  })
  layer.set(BASE_LAYER_PROPERTY, true)
  return layer
}

/**
 * Global shaded relief, hidden until the 3D map's Elevation surface asks for
 * it. Built alongside the imagery so it shares the same session token, and left
 * invisible so its tiles are never fetched in 2D.
 */
export const createArcGISHillshadeLayer = (): TileLayer<XYZ> => {
  const layer = createTileLayer(HILLSHADE_SERVICE, {
    className: 'ol-arcgis-hillshade-layer',
    title: 'ArcGIS World Hillshade',
    attributions: HILLSHADE_ATTRIBUTION,
    zIndex: IMAGERY_Z_INDEX,
  })
  layer.setVisible(false)
  layer.set(BASE_LAYER_PROPERTY, true)
  layer.set(RELIEF_LAYER_PROPERTY, true)
  return layer
}

/**
 * The roads/boundaries/labels half, unstyled. It is created empty and paints
 * only once `applyArcGISLabelsStyle` has loaded a style into it — the style URL
 * depends on the tenant, which is not known when the map first mounts, and the
 * imagery should not be held up waiting for it.
 */
export const createArcGISLabelsLayer = (): VectorTileLayer => {
  const layer = new VectorTileLayer({
    className: 'ol-arcgis-labels-layer',
    zIndex: LABELS_Z_INDEX,
    // 230 style layers of place names over farmland collide constantly without
    // this, and Esri's own hybrid basemap declutters too.
    declutter: true,
  })
  layer.set('title', 'ArcGIS Imagery Labels')
  layer.set(BASE_LAYER_PROPERTY, true)
  return layer
}

/*
 * applyStyle mutates the layer it is given, and it does so when its network work
 * finishes rather than when it is called. Two overlapping calls — a locale change
 * landing while the previous style is still loading — would otherwise race, and
 * whichever finished last would win regardless of which was asked for last,
 * leaving the map on a stale language or worldview.
 *
 * So applications are serialized per layer, and a request that has been
 * superseded before it starts is dropped rather than applied. The newest
 * configuration always ends up on the layer.
 */
const styleQueue = new WeakMap<VectorTileLayer, Promise<void>>()
const latestStyleRequest = new WeakMap<VectorTileLayer, symbol>()

/**
 * Loads the Mapbox-GL style into an existing labels layer. Safe to call again
 * when the locale, tenant or session token changes: it restyles the same layer
 * rather than building another one, so the style document is fetched once per
 * change and not once per render.
 *
 * Resolves when this call's turn is over — either because its style was applied,
 * or because a later call superseded it first. **Rejects if the style could not
 * be loaded**, so the caller can retry; swallowing it here would leave the map
 * without roads or labels and nothing upstream aware of it.
 */
export const applyArcGISLabelsStyle = (
  layer: VectorTileLayer,
  token: string,
  options: LabelsStyleOptions = {},
): Promise<void> => {
  const request = Symbol('arcgis-labels-style')
  latestStyleRequest.set(layer, request)

  const load = async () => {
    if (latestStyleRequest.get(layer) !== request) return

    await applyStyle(layer, labelsStyleUrl(token, options), {
      // Esri embeds a token in the style's `glyphs` url and serves its sprite
      // from a public CDN, but the vector tile service rejects every untokened
      // request.
      transformRequest: (url: string) => withArcGISToken(url, token),
    })
    layer.getSource()?.setAttributions(LABELS_ATTRIBUTION)
  }

  const next = (styleQueue.get(layer) ?? Promise.resolve()).then(load)
  // The caller gets the real outcome; the queue gets a settled copy, so one
  // failure cannot strand every application behind it.
  styleQueue.set(
    layer,
    next.catch(() => {}),
  )
  return next
}

/**
 * Points the imagery layer at the tile service for a given session. Called again
 * when the session is renewed, which re-requests the visible tiles once — cheap
 * against a twelve-hour session.
 *
 * A repeat call with the same token does nothing: setUrl invalidates the tile
 * cache, and a labels-style retry re-runs this first with the session unchanged.
 */
export const applyArcGISImagerySource = (
  layer: TileLayer<XYZ>,
  token: string,
): void => applyTileSource(layer, IMAGERY_SERVICE, token)

/** The same, for the shaded-relief layer. */
export const applyArcGISHillshadeSource = (
  layer: TileLayer<XYZ>,
  token: string,
): void => applyTileSource(layer, HILLSHADE_SERVICE, token)

const applyTileSource = (
  layer: TileLayer<XYZ>,
  service: string,
  token: string,
): void => {
  const source = layer.getSource()
  if (!source) return

  const url = tileUrl(service, token)
  if (source.getUrls()?.[0] === url) return

  source.setUrl(url)
}
